import type { MediaImage } from '@efdigital/ll-addon-data';
import { updateMediaImage } from '@efdigital/ll-admin-v2/data/builders/content/media-images';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';

import type MediaImagesService from '../../services/media-images';
import { Tags } from '../tags.gts';

interface Signature {
  Args: {
    mediaImage: MediaImage;
    saveLabel?: string;
    onSave: (image: MediaImage) => void;
  };
}

export class EditMediaImage extends Component<Signature> {
  @service declare store: Store;
  @service('media-images') declare mediaImages: MediaImagesService;

  @tracked keywordInput = '';
  @tracked uploadError: string | null = null;

  get saveLabel() {
    return this.args.saveLabel || 'Save Image';
  }

  updateTitle = (event: Event): void => {
    this.args.mediaImage.title = (event.target as HTMLInputElement).value;
  };

  updateAlt = (event: Event): void => {
    this.args.mediaImage.alt = (event.target as HTMLInputElement).value;
  };

  setKeywords = (keywords: string[]): void => {
    this.args.mediaImage.keywords = keywords;
  };

  saveAndUse = async (): Promise<void> => {
    try {
      const response = await this.store.request<
        ReactiveDataDocument<MediaImage>
      >(updateMediaImage(this.args.mediaImage, this.store));
      const image = response.content.data;

      this.args.onSave(image);
    } catch (error) {
      console.error('Failed to save keywords:', error);
      this.uploadError = 'Failed to save keywords';
    }
  };

  <template>
    <div class="image-uploader">
      {{! Title input }}
      <div class="mb-3">
        <label for="image-title" class="form-label">Title *</label>
        <input
          type="text"
          id="image-title"
          class="form-control"
          value={{@mediaImage.title}}
          {{on "input" this.updateTitle}}
          placeholder="Enter image title"
          required
        />
      </div>

      {{! Alt text input }}
      <div class="mb-3">
        <label for="image-alt" class="form-label">Alt Text</label>
        <input
          type="text"
          id="image-alt"
          class="form-control"
          value={{@mediaImage.alt}}
          {{on "input" this.updateAlt}}
          placeholder="Describe the image for accessibility"
        />
        <small class="form-text text-muted">
          If empty, title will be used. Describe the image for screen readers.
        </small>
      </div>

      {{! Keywords input - only show after upload }}
      <div class="mb-3">
        <label class="form-label">Keywords</label>
        <Tags
          @tags={{@mediaImage.keywords}}
          @onUpdate={{this.setKeywords}}
          @readonly={{false}}
          @icon="ph:tag"
        />
      </div>

      <div class="mt-3">
        {{! Action buttons }}
        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-success"
            {{on "click" this.saveAndUse}}
          >
            {{this.saveLabel}}
          </button>
        </div>
      </div>
    </div>
  </template>
}
